/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React, { useEffect, useRef, useState } from 'react';
import { IconRender } from '../IconRender';
import { DateFilterOptions } from '../Filters';
import { useLocalization } from '../../hooks';
import CustomDatePickerStyles from './CustomDatePicker.styles';

export interface CustomDatePickerProps extends DateFilterOptions {
    id: string;
    minDate?: Date;
    maxDate?: Date;
    selected?: Date;
    onChange: (date: Date) => void;
    dateFormat?: string;
    isEndDate?: boolean;
    isStartDate?: boolean;
    startDate?: Date;
    endDate?: Date;
    endDateErrorMessage?: string;
    closeOnScroll?: boolean;
    formatWeekDay?: number;
    staticDataTabIndex?: number;
    data?: any;
    limitedDates?: string[];
    defaultDate?: string;
    clearDates?: boolean;
}

export const CustomDatePicker = (props: CustomDatePickerProps) => {
    const {
        selected,
        onChange,
        startDate,
        endDate,
        isStartDate,
        isEndDate,
        minDate,
        maxDate,
        data,
        limitedDates,
        defaultDate,
        clearDates,
        ...rest
    } = props;
    const [inputValue, setInputValue] = useState<string>('');
    const [togglePosition, setTogglePosition] = useState<number>(0);
    const [currentMonth, setCurrentMonth] = useState<number>(new Date().getMonth() + 1);
    const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear());
    const [currentDay, setCurrentDay] = useState<number>(new Date().getDate());
    const [showMenu, setShowMenu] = useState<boolean>(false);
    const [showFrontArrow, setShowFrontArrow] = useState<boolean>(true);
    const [hasLimitedStartDateError, setHasLimitedStartDateError] = useState<boolean>(false);
    const [hasLimitedEndDateError, setHasLimitedEndDateError] = useState<boolean>(false);
    const i18n = useLocalization();
    const locale = i18n.getLocale();
    const selectedYearRef = useRef(null);
    const customDatePickerRef = useRef<any>(null);
    const menuRef = useRef<any>(null);

    // checks if default date exists and sets it to the input field and update state
    useEffect(() => {
        if (defaultDate && defaultDate.length > 0) {
            const dateSplit = defaultDate?.split('-');

            // expecting default date is in YYYY-MM-DD format
            const date = new Date(`${dateSplit[1]}/${dateSplit[2]}/${dateSplit[0]}`);
            // check if defaultDate is a date
            if (date instanceof Date && !isNaN(date.getTime())) {
                setInputValue(
                    date?.toLocaleDateString(locale, {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                    })
                );
                setCurrentMonth(date.getMonth() + 1);
                setCurrentYear(date.getFullYear());
                setCurrentDay(date.getDate());
                onChange(date);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultDate]);

    useEffect(() => {
        if (selectedYearRef.current && togglePosition === 2) {
            setTimeout(
                (selectedYearRef.current as any).scrollIntoView({
                    block: 'center',
                }),
                1000
            );
        }
    }, [currentYear, togglePosition]);

    // Define the types for the parameters
    type DateStatusParams = {
        newDate: Date;
        startDate?: Date;
        endDate?: Date;
        minDate?: Date;
        maxDate?: Date;
        limitedDates?: string[];
        day: number;
        yearOrMonthOrDay: 'year' | 'month' | 'day';
    };

    // Helper function to determine if a date should be disabled
    const getDateStatus = ({
        newDate,
        startDate,
        endDate,
        minDate,
        maxDate,
        limitedDates,
        day,
        yearOrMonthOrDay,
    }: DateStatusParams): boolean => {
        let isLimitedDate = true;

        // Check if the selected date is in the limitedDates array
        if (limitedDates && limitedDates.length > 0) {
            const limitDatesArray = limitedDates.map((date) => {
                const dateArray = date.split('-');
                return yearOrMonthOrDay === 'year'
                    ? dateArray[0]
                    : yearOrMonthOrDay === 'month'
                      ? `${dateArray[1].replace(/^0+/, '')}-${dateArray[0]}`
                      : `${dateArray[1].replace(/^0+/, '')}-${dateArray[0]}-${dateArray[2].replace(
                            /^0+/,
                            ''
                        )}`;
            });

            isLimitedDate = limitDatesArray.includes(
                yearOrMonthOrDay === 'year'
                    ? newDate.getFullYear().toString()
                    : yearOrMonthOrDay === 'month'
                      ? `${newDate.getMonth() + 1}-${newDate.getFullYear()}`
                      : `${currentMonth.toString()}-${currentYear}-${day.toString()}`
            );
        }

        const isAfterMinDate = minDate ? newDate >= minDate : true;
        const isBeforeMaxDate = maxDate ? newDate <= maxDate : true;
        const isAfterMaxDate = maxDate ? newDate > maxDate : false;

        // Check if the day matches either the start or end date
        let sameDate = false;
        if (isStartDate) {
            sameDate = (endDate && newDate.toDateString() === endDate.toDateString()) || false;
        } else {
            sameDate = (startDate && newDate.toDateString() === startDate.toDateString()) || false;
        }

        // Logic is added for month selection
        if (yearOrMonthOrDay === 'month') {
            if (isStartDate) {
                // same month can be selected but cannot select month after end month
                if (endDate) {
                    return (
                        newDate.getMonth() >= endDate.getMonth() + 1 &&
                        currentYear === endDate.getFullYear()
                    );
                }
            } else {
                // same month can be selected but cannot select month before start month
                if (startDate) {
                    return (
                        newDate.getMonth() <= startDate.getMonth() - 1 &&
                        currentYear === startDate.getFullYear()
                    );
                }
            }
        }

        // Logic is added for month selection
        if (yearOrMonthOrDay === 'year') {
            if (isStartDate && isAfterMinDate) {
                // same month can be selected but cannot select month after end month
                if (endDate) {
                    return newDate.getFullYear() >= endDate.getFullYear() + 1;
                }
            } else {
                // same month can be selected but cannot select month before start month
                if (startDate && isAfterMinDate) {
                    return newDate.getFullYear() <= startDate.getFullYear() - 1;
                }
            }
        }

        // Logic is added for date condition
        let notAllowDate = false;
        if (isStartDate) {
            notAllowDate = (endDate && newDate > endDate) || false;
        } else {
            notAllowDate = (startDate && newDate < startDate) || false;
        }

        // Return the disable status
        return (
            sameDate ||
            !isAfterMinDate ||
            !isBeforeMaxDate ||
            !isLimitedDate ||
            isAfterMaxDate ||
            notAllowDate
        );
    };

    // Function to generate an array of years
    const getYears = (start: number, end: number): number[] => {
        return Array.from({ length: end - start }, (_, i) => i + start);
    };

    // Function to generate an array of localized month names
    const getMonths = (
        lang: Intl.LocalesArgument,
        monthType: 'numeric' | '2-digit' | 'long' | 'short' | 'narrow' | undefined
    ) => {
        return Array.from({ length: 12 }, (_, i) => {
            // Create a new Date object for each month
            const date = new Date(2021, i, 1);
            const options: Intl.DateTimeFormatOptions = { month: monthType };
            return date?.toLocaleDateString(lang, options);
        });
    };

    // Generate an array of years from 1960 to the current year + 1
    const years: number[] = getYears(1960, new Date().getFullYear() + 1);

    const yearDisable = years.map((year) => {
        const newDate = new Date(
            `${currentMonth.toString().padStart(2, '0')}/${currentDay
                .toString()
                .padStart(2, '0')}/${year}`
        );

        const disable = getDateStatus({
            newDate,
            startDate,
            endDate,
            minDate,
            maxDate,
            limitedDates,
            day: 0,
            yearOrMonthOrDay: 'year',
        });

        return { year, disable };
    });

    // Generate an array of localized month names
    const months: string[] = getMonths(locale, 'short');

    // for the array months, add another key disbale to disable the months
    const monthsDisable = months.map((month, index) => {
        const monthIndex = index + 1;
        // Use the month's first day to avoid mismatches with currentDay.
        const newDate = new Date(currentYear, monthIndex - 1, 1);

        const disable = getDateStatus({
            newDate,
            startDate,
            endDate,
            minDate,
            maxDate,
            limitedDates,
            day: 0,
            yearOrMonthOrDay: 'month',
        });

        return { month, disable };
    });

    const monthsLong: string[] = getMonths(locale, 'long');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value;
        if (value.length === 2) {
            // auto-formatting logic is applied
            if (value.charAt(1) === '/') {
                value = '0' + value;
            }
        } else if (value.length === 5) {
            if (value.charAt(4) === '/') {
                value = value.substring(0, 3) + '0' + value.substring(3);
            }
        }

        setInputValue(value);
    };

    const handleClickOutside = (e: any) => {
        // check if click is outside od dropdown and toggle icon
        if (customDatePickerRef?.current && !customDatePickerRef.current.contains(e.target)) {
            // close drodown on click outside
            setTogglePosition(0);
            setShowMenu(false);
        }
    };

    // handle esc and tab key event outside date picker
    const handleKeyOut = (event: any) => {
        if (['Escape'].includes(event.key)) {
            setTogglePosition(0);
            setShowMenu(false);
        }
    };

    useEffect(() => {
        // event listener to check if click outside of component
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKeyOut);
        return () => {
            // remove the event listener
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyOut);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customDatePickerRef, menuRef]);

    // initial start date or end date already exist to be added to input field
    useEffect(() => {
        if (startDate && isStartDate) {
            setInputValue(
                startDate?.toLocaleDateString(locale, {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                })
            );
        } else if (endDate && isEndDate) {
            setInputValue(
                endDate?.toLocaleDateString(locale, {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                })
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // clear start date and end if clear dates is true
    useEffect(() => {
        if (clearDates) {
            setInputValue('');
        }
    }, [clearDates]);

    const handleEnter = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        if (minDate && (minDate as any) > new Date(value)) {
            setHasLimitedEndDateError(true);
        }

        if ((minDate && (minDate as any) <= new Date(value)) || !minDate) {
            // Check if the value is already in the desired format
            const isFormatDDMonthYYYY = /^\d{2}\s\w+\s\d{4}$/;
            const isFormatMonthDDYYYY = /^\w+\s\d{2},\s\d{4}$/;

            if (isFormatDDMonthYYYY.test(value) || isFormatMonthDDYYYY.test(value)) {
                // If already in the desired format, directly call onChange
                const [month, day, year] = value.split(/[\s,]+/);
                const newDate = new Date(`${month} ${day}, ${year}`);
                setCurrentMonth(newDate.getMonth() + 1);
                setCurrentYear(newDate.getFullYear());
                setCurrentDay(newDate.getDate());
                onChange(newDate);
                if (isStartDate) {
                    checkLimitedDateError(newDate, 'startDate');
                } else {
                    checkLimitedDateError(newDate, 'endDate');
                }
                return;
            }

            // Change date format when the input field loses focus
            if (value.length === 10) {
                if (locale === 'fr-CA') {
                    // Change DD/MM/YYYY to "DD Month YYYY"
                    const dateArray = value.split('/');
                    const month = dateArray[1];
                    const day = dateArray[0];
                    const year = dateArray[2];
                    const newDate = new Date(`${month}/${day}/${year}`);
                    setInputValue(
                        newDate?.toLocaleDateString(locale, {
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric',
                        })
                    );
                    setCurrentMonth(newDate.getMonth() + 1);
                    setCurrentYear(newDate.getFullYear());
                    setCurrentDay(newDate.getDate());
                    onChange(newDate);
                    if (isStartDate) {
                        checkLimitedDateError(newDate, 'startDate');
                    } else {
                        checkLimitedDateError(newDate, 'endDate');
                    }
                } else {
                    // Change MM/DD/YYYY to "Month DD, YYYY"
                    const dateArray = value.split('/');
                    const month = dateArray[0];
                    const day = dateArray[1];
                    const year = dateArray[2];
                    const newDate = new Date(`${month}/${day}/${year}`);
                    setInputValue(
                        newDate?.toLocaleDateString(locale, {
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric',
                        })
                    );
                    setCurrentMonth(newDate.getMonth() + 1);
                    setCurrentYear(newDate.getFullYear());
                    setCurrentDay(newDate.getDate());
                    onChange(newDate);
                    if (isStartDate) {
                        checkLimitedDateError(newDate, 'startDate');
                    } else {
                        checkLimitedDateError(newDate, 'endDate');
                    }
                }
            } else if (typeof value === 'string' && value.length > 0) {
                onChange(new Date(value));
            }
        }
    };

    const handleClickCalendarIcon = () => {
        if (isStartDate && startDate) {
            setCurrentMonth(startDate.getMonth() + 1);
            setCurrentYear(startDate.getFullYear());
            setCurrentDay(startDate.getDate());
        } else if (isEndDate && endDate) {
            setCurrentMonth(endDate.getMonth() + 1);
            setCurrentYear(endDate.getFullYear());
            setCurrentDay(endDate.getDate());
        }
        // Show/hide the date picker
        setShowMenu(true);
    };

    const handleClickCloseIcon = () => {
        // Close the date picker
        setShowMenu(false);
        setTogglePosition(0);
    };
    useEffect(() => {
        if (showMenu) {
            // Focus the menu or the first focusable element inside it
            if (menuRef.current) {
                menuRef.current.focus();
            }
        }
    }, [showMenu]);

    const handleToggleForward = () => {
        // Toggle backward to view months and years
        if (togglePosition !== 0) {
            setTogglePosition(togglePosition - 1);
        }
    };

    const handleToggleBackward = () => {
        // Toggle backward to view months and years
        if (togglePosition !== 2) {
            setTogglePosition(togglePosition + 1);
        }
    };

    const daysInMonth = (month: number, year: number) => {
        return new Date(year, month, 0).getDate();
    };

    const getFirstDayOfMonth = (month: number, year: number) => {
        return new Date(year, month - 1, 1).getDay(); // 0 for Sunday, 1 for Monday, etc.
    };

    const getCurrentMonthDays = () => {
        let currentDate = new Date();

        if (isStartDate && startDate) {
            currentDate = startDate;
        } else if (isEndDate && endDate) {
            currentDate = endDate;
        }

        const year = currentYear || currentDate.getFullYear();
        const month = currentMonth || currentDate.getMonth() + 1;
        const totalDays = daysInMonth(month, year);
        const firstDayOfMonth = getFirstDayOfMonth(month, year);

        // Calculate the number of days to display from the previous month
        const daysFromPrevMonth = firstDayOfMonth;

        // Calculate the number of days to display from the next month
        const daysFromNextMonth = 42 - totalDays - daysFromPrevMonth;

        // Generate an array of all the days in the current month
        const daysArray = Array.from(
            { length: totalDays + daysFromPrevMonth + daysFromNextMonth },
            (_, i) => {
                if (i < daysFromPrevMonth) {
                    // Days from the previous month
                    const prevMonth = month === 1 ? 12 : month - 1;
                    const prevYear = month === 1 ? year - 1 : year;
                    const prevMonthDays = daysInMonth(prevMonth, prevYear);
                    const day = prevMonthDays - daysFromPrevMonth + i + 1;
                    return { day: day, currentMonth: false, disable: false };
                } else if (i >= daysFromPrevMonth && i < totalDays + daysFromPrevMonth) {
                    // Days from the current month
                    const day = i - daysFromPrevMonth + 1;
                    const newDate = new Date(
                        `${currentMonth.toString().padStart(2, '0')}/${day
                            .toString()
                            .padStart(2, '0')}/${currentYear}`
                    );

                    const disable = getDateStatus({
                        newDate,
                        startDate,
                        endDate,
                        minDate,
                        maxDate,
                        limitedDates,
                        day,
                        yearOrMonthOrDay: 'day',
                    });

                    return { day: day, currentMonth: true, disable: disable };
                } else {
                    // Days from the next month
                    const day = i - (totalDays + daysFromPrevMonth) + 1;
                    return { day: day, currentMonth: false, disable: false };
                }
            }
        );

        return daysArray;
    };

    const handleDateChange = (day: number) => {
        const newDate = new Date(
            `${currentMonth.toString().padStart(2, '0')}/${day
                .toString()
                .padStart(2, '0')}/${currentYear}`
        );

        setInputValue(
            newDate?.toLocaleDateString(locale, {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
            })
        );
        setCurrentDay(day);
        onChange(newDate);
        isStartDate ? setHasLimitedStartDateError(false) : setHasLimitedEndDateError(false);

        setShowMenu(false);
    };

    const handleMonthChange = (month: number) => {
        setCurrentMonth(month);
        setShowFrontArrow(true);
        setTogglePosition(togglePosition - 1);
    };

    const handleYearChange = (year: number) => {
        setCurrentYear(year);
        setShowFrontArrow(false);
        setTogglePosition(togglePosition - 1);
    };

    // Function to render the icon based on the provided options
    const renderErrorIcon = () => (
        <IconRender
            showSpan={true}
            className={`error-message-icon-${
                rest?.errorIconPosition === 'Left' ? 'left' : 'right'
            }`}
            iconType={rest?.errorIconType}
            iconName={rest?.errorIcon}
            iconClassName={''}
            imageUrl={rest?.errorImageUrl}
            imageClassName={'error-message-image'}
            imageAlt={'error-message'}
            staticDataTabIndex={rest.staticDataTabIndex}
        />
    );

    const renderIcon = () => (
        <div className="icons">
            {!showMenu && (
                <div className="calendar-icon" onClick={handleClickCalendarIcon}>
                    <IconRender
                        showSpan={true}
                        className={`custom-date-picker-icon-${
                            rest?.datePickerIconPosition === 'Left' ? 'left' : 'right'
                        } open`}
                        iconType={rest?.datePickerIconType}
                        iconName={rest.datePickerIcon}
                        imageUrl={rest?.datePickerImage}
                        imageClassName={'custom-date-picker-image'}
                        imageAlt={'dropdown-toggle-open'}
                        staticDataTabIndex={rest.staticDataTabIndex}
                        onKeyDown={(e: any) =>
                            e.key === 'Enter' ? handleClickCalendarIcon() : null
                        }
                    />
                </div>
            )}
            {showMenu && (
                <div className="close-icon" onClick={handleClickCloseIcon}>
                    <IconRender
                        showSpan={true}
                        className={`custom-date-picker-icon-${
                            rest?.closeDatePickerIconPosition === 'Left' ? 'left' : 'right'
                        } close`}
                        iconType={rest?.closeDatePickerIconType}
                        iconName={rest?.closeDatePickerIcon}
                        imageUrl={rest?.closeDatePickerImage}
                        imageClassName={'custom-date-picker-image'}
                        imageAlt={'dropdown-toggle-open'}
                        staticDataTabIndex={rest.staticDataTabIndex}
                        onKeyDown={(e: any) => (e.key === 'Enter' ? handleClickCloseIcon() : null)}
                    />
                </div>
            )}
        </div>
    );

    let hasStartDateError;
    let hasEndDateError;

    // function to check if the selected date is in the limitedDates array
    const checkLimitedDateError = (newDate: Date, dateType: string) => {
        const getDate = newDate.getDate();
        const getMonth = newDate.getMonth() + 1;
        const getYear = newDate.getFullYear();
        if (limitedDates && limitedDates.length > 0) {
            const isLimitedDate = limitedDates.includes(
                `${getYear}-${getMonth.toString().padStart(2, '0')}-${getDate
                    .toString()
                    .padStart(2, '0')}`
            );
            if (!isLimitedDate && dateType === 'startDate') {
                setHasLimitedStartDateError(true);
            } else if (!isLimitedDate && dateType === 'endDate') {
                setHasLimitedEndDateError(true);
            } else {
                dateType === 'startDate'
                    ? setHasLimitedStartDateError(false)
                    : setHasLimitedEndDateError(false);
            }
        }
    };

    if (startDate) {
        hasEndDateError =
            endDate && startDate > endDate && startDate && isEndDate && rest?.endDateErrorMessage;
    }

    if (endDate) {
        hasStartDateError =
            startDate &&
            startDate > endDate &&
            endDate &&
            isStartDate &&
            rest?.startDateErrorMessage;
    }

    const hasError = hasStartDateError || hasEndDateError;

    return (
        <div
            css={css`
                ${CustomDatePickerStyles}
            `}
        >
            <div
                className={`custom-date-picker-input ${
                    hasError ? 'custom-date-picker-input-error' : ''
                }`}
                ref={customDatePickerRef}
            >
                {rest?.datePickerIconPosition === 'Left' ? renderIcon() : null}
                <input
                    data-testid="input-date-picker"
                    type="text"
                    value={inputValue}
                    onChange={handleChange}
                    onKeyDown={(e: any) => (e.key === 'Enter' ? handleEnter(e) : null)}
                    aria-label={
                        inputValue
                            ? `${i18n.t('date_picker')} ${inputValue}`
                            : i18n.t('date_picker')
                    }
                />
                {rest?.datePickerIconPosition === 'Right' ? renderIcon() : null}
                {showMenu && (
                    <>
                        <div
                            className="custom-date-picker-menu"
                            ref={menuRef}
                            tabIndex={rest.staticDataTabIndex}
                        >
                            <div className="datepicker-header">
                                <div className="month-year">
                                    {togglePosition === 0 ? `${monthsLong[currentMonth - 1]} ` : ''}
                                    {togglePosition !== 2 ? currentYear : ''}
                                </div>
                            </div>
                            <div className={`arrows-toggle ${togglePosition === 2 ? 'end' : ''}`}>
                                {togglePosition < 2 && (
                                    <div className="back-arrow" onClick={handleToggleBackward}>
                                        <IconRender
                                            showSpan={true}
                                            className={'custom-date-picker-icon-back-arrow'}
                                            iconType={rest?.backArrowIconType}
                                            iconName={rest?.backArrowIcon}
                                            imageUrl={rest?.backArrowImage}
                                            imageClassName={'custom-date-picker-image'}
                                            imageAlt={'dropdown-toggle-open'}
                                            staticDataTabIndex={rest.staticDataTabIndex}
                                            onKeyDown={(e: any) =>
                                                e.key === 'Enter' ? handleToggleBackward() : null
                                            }
                                        />
                                    </div>
                                )}
                                {togglePosition > 0 && showFrontArrow && (
                                    <div className="front-arrow" onClick={handleToggleForward}>
                                        <IconRender
                                            showSpan={true}
                                            className={'custom-date-picker-icon-front-arrow'}
                                            iconType={rest?.forwardArrowIconType}
                                            iconName={rest?.forwardArrowIcon}
                                            imageUrl={rest?.forwardArrowImage}
                                            imageClassName={'custom-date-picker-image'}
                                            imageAlt={'dropdown-toggle-open'}
                                            staticDataTabIndex={rest.staticDataTabIndex}
                                            onKeyDown={(e: any) =>
                                                e.key === 'Enter' ? handleToggleForward() : null
                                            }
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="datepicker-content">
                                {togglePosition === 0 && (
                                    <>
                                        <div className="date-heading">
                                            <span className="day-heading">
                                                {i18n.t('calendar_sunday')}
                                            </span>
                                            <span className="day-heading">
                                                {i18n.t('calendar_monday')}
                                            </span>
                                            <span className="day-heading">
                                                {i18n.t('calendar_tuesday')}
                                            </span>
                                            <span className="day-heading">
                                                {i18n.t('calendar_wednesday')}
                                            </span>
                                            <span className="day-heading">
                                                {i18n.t('calendar_thursday')}
                                            </span>
                                            <span className="day-heading">
                                                {i18n.t('calendar_friday')}
                                            </span>
                                            <span className="day-heading">
                                                {i18n.t('calendar_saturday')}
                                            </span>
                                        </div>
                                        <div className="dates">
                                            {getCurrentMonthDays().map((item, index) => (
                                                <div
                                                    key={index}
                                                    className={`date ${
                                                        !item.currentMonth
                                                            ? 'not-current-month'
                                                            : ''
                                                    } ${item.disable ? 'disable' : ''}`}
                                                >
                                                    <span
                                                        className={`day ${
                                                            !item.currentMonth
                                                                ? 'not-current-month'
                                                                : ''
                                                        } ${
                                                            item.day === currentDay &&
                                                            item.currentMonth
                                                                ? 'selected'
                                                                : ''
                                                        } ${item.disable ? 'disable' : ''}`}
                                                        tabIndex={
                                                            !item.currentMonth || item.disable
                                                                ? -1
                                                                : rest.staticDataTabIndex
                                                        }
                                                        onClick={() => handleDateChange(item.day)}
                                                        onKeyDown={(e: any) => {
                                                            if (e.key === 'Enter') {
                                                                handleDateChange(item.day);
                                                            }
                                                        }}
                                                    >
                                                        {item.day}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                                {togglePosition === 1 && (
                                    <div className="months">
                                        {months?.map((option, index) => (
                                            <div
                                                key={option}
                                                className={`month ${
                                                    index === currentMonth - 1 ? 'selected' : ''
                                                } ${monthsDisable[index].disable ? 'disable' : ''}`}
                                            >
                                                <span
                                                    className={`${
                                                        monthsDisable[index].disable
                                                            ? 'disable'
                                                            : ''
                                                    }`}
                                                    tabIndex={
                                                        monthsDisable[index].disable
                                                            ? -1
                                                            : rest.staticDataTabIndex
                                                    }
                                                    onClick={() => handleMonthChange(index + 1)}
                                                    onKeyDown={(e: any) => {
                                                        if (e.key === 'Enter') {
                                                            handleMonthChange(index + 1);
                                                        }
                                                    }}
                                                >
                                                    {option}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {togglePosition === 2 && (
                                    <div className="years">
                                        {years?.map((option, index) => (
                                            <div
                                                key={option}
                                                className={`year ${
                                                    option === currentYear ? 'selected' : ''
                                                } ${yearDisable[index].disable ? 'disable' : ''}`}
                                                ref={
                                                    option === currentYear ? selectedYearRef : null
                                                }
                                            >
                                                <span
                                                    className={`${
                                                        yearDisable[index].disable ? 'disable' : ''
                                                    }`}
                                                    tabIndex={
                                                        yearDisable[index].disable
                                                            ? -1
                                                            : rest.staticDataTabIndex
                                                    }
                                                    onClick={() => handleYearChange(option)}
                                                    onKeyDown={(e: any) =>
                                                        e.key === 'Enter'
                                                            ? handleYearChange(option)
                                                            : null
                                                    }
                                                >
                                                    {option}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
            <span className="date-picker-bottom-label">{i18n.t('calendar_default_format')}</span>
            <span className="custom-date-picker-error-message" data-testid="date-picker-error">
                {hasStartDateError && isStartDate && (
                    <>
                        {rest?.errorIconPosition === 'Left' ? renderErrorIcon() : null}
                        {isStartDate && rest?.startDateErrorMessage}
                        {rest?.errorIconPosition === 'Right' ? renderErrorIcon() : null}
                    </>
                )}
                {hasEndDateError && isEndDate && (
                    <>
                        {rest?.errorIconPosition === 'Left' ? renderErrorIcon() : null}
                        {isEndDate && rest?.endDateErrorMessage}
                        {rest?.errorIconPosition === 'Right' ? renderErrorIcon() : null}
                    </>
                )}
                {hasLimitedStartDateError && isStartDate && (
                    <>
                        {rest?.errorIconPosition === 'Left' ? renderErrorIcon() : null}
                        {rest?.limitDateStartErrorMessage}
                        {rest?.errorIconPosition === 'Right' ? renderErrorIcon() : null}
                    </>
                )}
                {hasLimitedEndDateError && isEndDate && (
                    <>
                        {rest?.errorIconPosition === 'Left' ? renderErrorIcon() : null}
                        {rest?.limitDateEndErrorMessage}
                        {rest?.errorIconPosition === 'Right' ? renderErrorIcon() : null}
                    </>
                )}
            </span>
        </div>
    );
};
